// F2-a: API owner verso api-cloud. ownerLogin (email+password+TOTP) e
// fetchOwnerDashboard (vista "oggi" dei 3 hotel). Shape allineata agli
// endpoint apps/api-cloud /auth/owner-login e /owner/dashboard.
import { cloudApi } from './cloud-client.js';

export type HotelToday = {
  tenantId: string;
  name: string;
  slug: string;
  online: boolean;
  lastHeartbeatAt: string | null;
  roomsTotal: number;
  roomsOccupied: number;
  // Incasso del giorno in euro come stringa decimale (es. "1234.50"),
  // serializzato dal cloud con .toFixed(2).
  todayRevenue: string;
};

export type OwnerInfo = { id: string; email: string };

// Su 401 con error 'totp_required' la UI mostra il campo codice e ri-invia
// con totpCode. invalid_credentials / totp_invalid -> messaggi chiari.
export async function ownerLogin(body: { email: string; password: string; totpCode?: string }) {
  return cloudApi<{ ok: true; owner: OwnerInfo }>('/auth/owner-login', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function fetchOwnerDashboard() {
  return cloudApi<{ hotels: HotelToday[]; generatedAt: string }>('/owner/dashboard');
}

// ---------------------------------------------------------------------------
// F2-c: gestione hotel (detail + CRUD). Shape allineata a api-cloud
// /owner/hotels/:tenantId/...
// ---------------------------------------------------------------------------

export type AccessMethod = 'code' | 'card' | 'mixed' | 'key';

export type HotelFeatures = {
  enable_day_use?: boolean;
  enable_overnight?: boolean;
  enable_room_service?: boolean;
  enable_alloggiati?: boolean;
  enable_garage?: boolean;
  card_provider?: 'mifare' | 'hid' | 'custom' | null;
};

export type HotelTenant = {
  id: string;
  name: string;
  slug: string;
  address: string | null;
  city: string | null;
  province: string | null;
  cap: string | null;
  phone: string | null;
  email: string | null;
  vatNumber: string | null;
  accessMethod: AccessMethod;
  featuresJson: HotelFeatures;
};

export type HotelRoom = {
  id: string;
  tenantId: string;
  roomTypeId: string;
  code: string;
  floor: string;
  building: string;
  status: 'free' | 'reserved' | 'occupied' | 'dirty' | 'out_of_order';
  deletedAt: string | null;
};

export type HotelRoomType = {
  id: string;
  tenantId: string;
  code: string;
  label: string;
  description: string | null;
  maxOccupancy: number;
  cleaningBufferMinutes: number;
  pricingRuleId: string | null;
  displayOrder: number;
  active: boolean;
};

export type HotelPricingRule = {
  id: string;
  name: string;
  base2hPrice: string;
  hour3Supplement: string;
  hour4plusUnit: string;
  overnightPrice: string;
  weekendSurchargePct: number;
  holidaySurchargePct: number;
};

export type HotelUser = {
  id: string;
  tenantId: string;
  email: string;
  fullName: string;
  username: string | null;
  role: 'reception' | 'housekeeping' | 'admin' | 'owner';
  active: boolean;
  hasPin: boolean;
};

export type HotelDetail = {
  tenant: HotelTenant;
  rooms: HotelRoom[];
  roomTypes: HotelRoomType[];
  pricingRules: HotelPricingRule[];
  users: HotelUser[];
};

export async function fetchHotelDetail(tenantId: string) {
  return cloudApi<HotelDetail>(`/owner/hotels/${tenantId}`);
}

export type HotelPatch = {
  name?: string;
  address?: string | null;
  city?: string | null;
  province?: string | null;
  cap?: string | null;
  phone?: string | null;
  email?: string | null;
  vatNumber?: string | null;
  accessMethod?: AccessMethod;
  features?: HotelFeatures;
};

export async function patchHotel(tenantId: string, patch: HotelPatch) {
  return cloudApi<{ tenant: HotelTenant }>(`/owner/hotels/${tenantId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export type RoomCreateInput = {
  roomTypeId: string;
  code: string;
  floor: string;
  building: string;
};

export async function createHotelRoom(tenantId: string, body: RoomCreateInput) {
  return cloudApi<{ room: HotelRoom }>(`/owner/hotels/${tenantId}/rooms`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export type RoomPatchInput = {
  roomTypeId?: string;
  code?: string;
  floor?: string;
  building?: string;
  status?: HotelRoom['status'];
  active?: boolean;
};

export async function patchHotelRoom(roomId: string, body: RoomPatchInput) {
  return cloudApi<{ room: HotelRoom }>(`/owner/rooms/${roomId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export type RoomTypePatchInput = {
  label?: string;
  maxOccupancy?: number;
  cleaningBufferMinutes?: number;
  active?: boolean;
  pricing?: {
    base2hPrice?: number;
    hour3Supplement?: number;
    hour4plusUnit?: number;
    overnightPrice?: number;
    weekendSurchargePct?: number;
    holidaySurchargePct?: number;
  };
};

export async function patchHotelRoomType(roomTypeId: string, body: RoomTypePatchInput) {
  return cloudApi<{ roomType: HotelRoomType; pricingRule: HotelPricingRule | null }>(
    `/owner/room-types/${roomTypeId}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

export type UserCreateInput = {
  email: string;
  fullName: string;
  username?: string;
  role: 'reception' | 'housekeeping' | 'admin';
};

export type GeneratedCredentials = { password: string; pin: string };

export async function createHotelUser(tenantId: string, body: UserCreateInput) {
  return cloudApi<{ user: HotelUser; credentials: GeneratedCredentials }>(
    `/owner/hotels/${tenantId}/users`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export type UserPatchInput = {
  fullName?: string;
  role?: 'reception' | 'housekeeping' | 'admin';
  active?: boolean;
};

export async function patchHotelUser(userId: string, body: UserPatchInput) {
  return cloudApi<{ user: HotelUser }>(`/owner/users/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function resetHotelUserPin(userId: string, pin?: string) {
  return cloudApi<{ pin: string }>(`/owner/users/${userId}/reset-pin`, {
    method: 'POST',
    body: JSON.stringify(pin ? { pin } : {}),
  });
}
